import moment from 'moment'

const REQUIRED_FIELD_ERROR_MESSAGE = 'Please enter a valid value'

export const DATE_TIME_FORMAT = 'MMM DD hh:mm:ss A'

function dateComparatorForDesc(date1, date2) {
  var date1Number = date1 && new Date(date1).getTime()
  var date2Number = date2 && new Date(date2).getTime()

  if (date1Number == null && date2Number == null) {
    return 0
  }

  if (date1Number == null) {
    return 1
  } else if (date2Number == null) {
    return -1
  }

  return date2Number - date1Number
}

function dateComparatorForAsc(date1, date2) {
  var date1Number = date1 && new Date(date1).getTime()
  var date2Number = date2 && new Date(date2).getTime()

  if (date1Number == null && date2Number == null) {
    return 0
  }

  if (date1Number == null) {
    return -1
  } else if (date2Number == null) {
    return 1
  }

  return date1Number - date2Number
}

export function replaceItemInArray(items, item, findItem) {
  const idx = items.findIndex(findItem)

  items[idx] = item
}

export const dateTimeFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'short'
})

export const dateFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
})

export function getDateSortOperator(getValue = undefined, sort = 'desc') {
  const sortFunc =
    sort === 'desc' ? dateComparatorForDesc : dateComparatorForAsc

  if (getValue) {
    return function(dataA, dataB) {
      return sortFunc(getValue(dataA), getValue(dataB))
    }
  }

  return sortFunc
}

export function capitlizeFirstLetter(str) {
  return str && str.length > 0 ? `${str[0].toUpperCase()}${str.slice(1)}` : ''
}

export function formatDate(date) {
  return moment(new Date(date)).format('MMM DD, YYYY')
}

export function formatDateTime(date) {
  return moment(new Date(date)).format(DATE_TIME_FORMAT)
}

export function formatFraudDateTime(date) {
  return moment(date).format('MMM DD, YYYY, h:mm A')
}

export function timeAgo(date) {
  if (!date) {
    return ''
  }

  return moment(new Date(date)).fromNow()
}

// Queue-age emphasis for review lists: amber past warnDays, red past dangerDays.
export function queueAgeClass(date, { warnDays = 14, dangerDays = 30 } = {}) {
  if (!date) {
    return ''
  }

  const days = Math.floor((Date.now() - new Date(date).getTime()) / 86400000)

  if (days > dangerDays) {
    return 'text-danger fw-medium'
  }

  if (days > warnDays) {
    return 'text-warning fw-medium'
  }

  return ''
}

export function convertDurationInSecsToTimeFormat(durationInSecs) {
  return moment.utc(durationInSecs * 1000).format('HH:mm:ss')
}

export function formatDateInclusive(date) {
  if (!date || date === 'N/A') {
    return 'N/A'
  }

  const result = moment.parseZone(date)
  if (!result.isValid()) {
    return 'N/A'
  }

  result.subtract(1, 's')
  return result.format('MMM DD, YYYY')
}

export function formatAmount(amount) {
  if (amount === undefined) { 
    return "N/A"
  }
  
  var formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  })
  return formatter.format(amount)
}

export function formatPercentage(amount) {
  if (amount === undefined) { 
    return "N/A"
  }

  var formatter = new Intl.NumberFormat('en-US', {
    style: 'percent',
    maximumFractionDigits: 2
  })
  return formatter.format(amount)
}

export function sortAlphabeticallyComparison(string1, string2) {
  if (string1 < string2) {
    return -1
  }

  if (string1 > string2) {
    return 1
  }

  // strings must be equal
  return 0
}

export function sortByKey(key) {
  return (a, b) => sortAlphabeticallyComparison((a[key] || '').toUpperCase(), (b[key] || '').toUpperCase())
}

// Unordered array equality.
function sameMembers(a, b) {
  return a.length === b.length && a.every(value => b.includes(value))
}

// CMultiSelect echoes a change event when options and values sync up on load.
// Returns the selected values, or null when they match the current model.
export function changedSelection(selected, current) {
  const values = selected.map(option => option.value)
  return sameMembers(values, current || []) ? null : values
}

// CMultiSelect emits an array of option objects even in single-select mode.
export function firstSelectedValue(selected, fallback = null) {
  return selected.length ? selected[0].value : fallback
}

// First selected option as { id, label }, or null when the selection is cleared.
export function firstSelectedOption(selected) {
  return selected.length ? { id: selected[0].value, label: selected[0].label } : null
}

// CoreUI 5 CFormSelect dropped the v3 placeholder prop and skips the value
// binding for falsy models, so the browser would show the first real option.
// Prepend an empty-value option so the display matches an unset model.
export function withPlaceholderOption(options, label = 'Please select') {
  return [{ value: '', label }, ...(options ?? [])]
}

// window.open() keeps window.opener set (anchors get implicit noopener,
// window.open does not). Browsers special-case the noopener token, so this
// still opens a tab, not a popup.
export function openInNewTab(url) {
  window.open(url, '_blank', 'noopener')
}

export const sortByLabel = sortByKey('label')

export const sortByName = sortByKey('name')

// CoreUI 5 form controls ignore v-model modifiers (.trim/.number leak into the
// DOM as a modelmodifiers attribute), so normalize values at submit time instead.
export function trimmed(value) {
  return typeof value === 'string' ? value.trim() : value
}

export function toNumberOrNull(value) {
  if (value == null || value === '') {
    return null
  }

  const number = Number(value)

  return Number.isNaN(number) ? value : number
}

// Group items into { label, options } groups keyed by getGroupName,
// preserving first-seen order.
function groupIntoLabeledOptions(items, getGroupName) {
  const groups = []
  const byName = {}

  items.forEach(item => {
    const name = getGroupName(item) || ''

    if (!byName[name]) {
      byName[name] = { label: name, options: [] }
      groups.push(byName[name])
    }

    byName[name].options.push({ value: item.id, label: item.name })
  })

  return groups
}

export function groupTrafficSourcesByType(sources) {
  return groupIntoLabeledOptions(sources, source => source?.trafficSourceType)
}

export function groupCategoriesByParent(categories) {
  return groupIntoLabeledOptions(categories, category => category?.parentCategory?.name)
}

export function getDateRangeInTimeZone(timeRange) {
  const standardTimeZoneId = Intl.DateTimeFormat().resolvedOptions().timeZone
  // Time is sent in UTC
  // If user selects 3PM in PST(UTC-08:00): the time which would be sent is 15:00 - 08:00 = 07:00 
  const startDate = moment.tz(moment(timeRange.startDate).format('YYYY-MM-DD HH:mm:ss'), standardTimeZoneId).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')
  const endDate = moment.tz(moment(timeRange.endDate).format('YYYY-MM-DD HH:mm:ss'), standardTimeZoneId).utc().format('YYYY-MM-DDTHH:mm:ss[Z]')

  return { startDate, endDate }
}

export function splitLines(t) {
  return t.split(/\r\n|\r|\n/)
}

// Escape &, <, >, ", ' for safe interpolation into HTML strings.
export function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

export function exportPaymentHistoryCsv({ rows, fields, filePrefix }) {
  if (!rows.length) {
    return
  }

  const headers = fields.map(f => f.label || f.key)
  const keys = fields.map(f => f.key)

  const csvRows = [headers.join(',')]
  for (const row of rows) {
    const values = keys.map(key => {
      let val = ''
      if (key === 'paymentDate') {
        val = row.formattedPaymentDate || ''
      } else {
        val = row[key] != null ? row[key] : ''
      }

      const escaped = String(val).replace(/"/g, '""')
      return `"${escaped}"`
    })
    csvRows.push(values.join(','))
  }

  const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `${filePrefix}-payment-history-${new Date().toISOString().slice(0, 10)}.csv`
  link.click()
  URL.revokeObjectURL(url)
}

export { REQUIRED_FIELD_ERROR_MESSAGE }
